import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { db } from '../firebase';
import { collection, query, where, orderBy, limit, getDocs, Timestamp } from 'firebase/firestore';
import { Logo } from '../components/Logo';

function Asgard() {
  const navigate = useNavigate();
  const { currentUser, userData, logout } = useAuth();
  const [stats, setStats] = useState({
    todayCount: 0,
    monthCount: 0,
    totalBooks: 0,
    archivedBooks: 0,
    nextBarcode: userData?.lastBarcodeUsed + 1 || 125864,
    recentBooks: [],
    readyForPrint: 0,
    readyForExport: 0,
    needsReview: 0
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadDashboardStats();
    
    const handleFocus = () => {
      loadDashboardStats();
    };
    
    window.addEventListener('focus', handleFocus);
    
    // Also refresh stats every 10 seconds for real-time updates
    const interval = setInterval(loadDashboardStats, 10000);
    
    return () => {
      window.removeEventListener('focus', handleFocus);
      clearInterval(interval);
    };
  }, [currentUser]);

  const loadDashboardStats = async () => {
    if (!currentUser) return;
    
    setLoading(true);
    try {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      
      const booksRef = collection(db, 'users', currentUser.uid, 'books');
      const archivedRef = collection(db, 'users', currentUser.uid, 'archives');
      const flaggedRef = collection(db, 'users', currentUser.uid, 'flaggedBooks');
      
      // Today's books from ACTIVE books collection
      const todayBooksQuery = query(
        booksRef,
        where('createdAt', '>=', Timestamp.fromDate(today)),
        orderBy('createdAt', 'desc')
      );
      const todayBooksSnapshot = await getDocs(todayBooksQuery);
      
      // Today's books from ARCHIVES collection (already processed/archived today)
      const todayArchivedQuery = query(
        archivedRef,
        where('createdAt', '>=', Timestamp.fromDate(today))
      );
      const todayArchivedSnapshot = await getDocs(todayArchivedQuery);
      
      // Combined today count = books + archives processed today
      const todayCount = todayBooksSnapshot.size + todayArchivedSnapshot.size;
      
      // All active books
      const allBooksQuery = query(booksRef, orderBy('createdAt', 'desc'));
      const allBooksSnapshot = await getDocs(allBooksQuery);
      
      // Archived books count
      const archivedSnapshot = await getDocs(archivedRef);
      
      // Flagged books count (Loki's Chamber)
      const flaggedSnapshot = await getDocs(flaggedRef);
      
      // Recent books for display
      const recentQuery = query(
        booksRef,
        orderBy('createdAt', 'desc'),
        limit(10)
      );
      const recentSnapshot = await getDocs(recentQuery);
      const recentBooks = recentSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        createdAt: doc.data().createdAt?.toDate?.() || new Date()
      }));

      // All active books = ready for print and ready for MARC export
      const totalActiveBooks = allBooksSnapshot.size;

      setStats({
        todayCount: todayCount,
        monthCount: totalActiveBooks,
        totalBooks: totalActiveBooks,
        archivedBooks: archivedSnapshot.size,
        nextBarcode: userData?.lastBarcodeUsed + 1 || 125864,
        recentBooks: recentBooks,
        readyForPrint: totalActiveBooks,
        readyForExport: totalActiveBooks,
        needsReview: flaggedSnapshot.size
      });
    } catch (error) {
      console.error('Error loading stats:', error);
      setStats({
        todayCount: 0,
        monthCount: 0,
        totalBooks: 0,
        archivedBooks: 0,
        nextBarcode: userData?.lastBarcodeUsed + 1 || 125864,
        recentBooks: [],
        readyForPrint: 0,
        readyForExport: 0,
        needsReview: 0
      });
    }
    setLoading(false);
  };

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/');
    } catch (error) {
      console.error('Failed to log out', error);
    }
  };

  const getRelativeTime = (date) => {
    const now = new Date();
    const diffInHours = Math.floor((now - date) / (1000 * 60 * 60));
    
    if (diffInHours < 1) return 'Just now';
    if (diffInHours < 24) return `${diffInHours}h ago`;
    
    const diffInDays = Math.floor(diffInHours / 24);
    if (diffInDays < 7) return `${diffInDays}d ago`;
    
    return date.toLocaleDateString();
  };

  return (
    <div className="min-h-screen bg-slate-100">
      {/* Header */}
      <header className="bg-gradient-to-r from-purple-900 to-indigo-900 text-white shadow-xl border-b border-purple-700/50">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 sm:gap-0">
            <div className="flex items-center space-x-2 sm:space-x-4">
              <Logo size={40} showText={false} />
              <div className="flex items-center space-x-3">
                <h1 className="text-lg sm:text-2xl font-bold tracking-wider">HEIMDL</h1>
                <span className="text-purple-300 hidden sm:inline">|</span>
                <span className="text-purple-200 hidden sm:inline">{userData?.schoolName || 'Loading...'}</span>
              </div>
            </div>
            <div className="flex items-center space-x-3 sm:space-x-6">
              <div className="text-right hidden md:block">
                <p className="text-sm text-purple-300">Last Barcode Used</p>
                <p className="text-xl font-mono font-bold">{userData?.lastBarcodeUsed || '125863'}</p>
              </div>
              <button
                onClick={handleLogout}
                className="bg-purple-700 hover:bg-purple-600 px-3 py-1.5 sm:px-4 sm:py-2 rounded-lg transition-colors text-sm sm:text-base"
              >
                Leave Asgard
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 py-8">
        <div className="space-y-8">
          {/* Welcome Section */}
          <div className="bg-gradient-to-r from-purple-600 to-indigo-600 rounded-xl p-8 text-white shadow-xl border border-purple-500/30">
            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4 sm:space-x-4 mb-4">
              <Logo size={60} showText={false} />
              <div>
                <h1 className="text-2xl sm:text-4xl font-bold mb-2">Welcome to Asgard</h1>
                <p className="text-purple-100">The realm where all library knowledge converges</p>
              </div>
            </div>
          </div>

          {/* Stats Grid */}
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-6 gap-4">
            <div className="bg-slate-200 rounded-lg shadow-lg p-4 border border-slate-300 hover:border-purple-400/50 transition-all hover:shadow-xl">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-slate-600 text-sm">Today's Books</p>
                  <p className="text-2xl font-bold text-slate-800">
                    {loading ? '...' : stats.todayCount}
                  </p>
                </div>
                <span className="text-3xl">📚</span>
              </div>
            </div>

            <div className="bg-slate-200 rounded-lg shadow-lg p-4 border border-slate-300 hover:border-indigo-400/50 transition-all hover:shadow-xl">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-slate-600 text-sm">Total Active</p>
                  <p className="text-2xl font-bold text-slate-800">
                    {loading ? '...' : stats.totalBooks}
                  </p>
                </div>
                <span className="text-3xl">📖</span>
              </div>
            </div>

            <div className="bg-slate-200 rounded-lg shadow-lg p-4 border border-slate-300 hover:border-green-400/50 transition-all hover:shadow-xl">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-slate-600 text-sm">Ready to Print</p>
                  <p className="text-2xl font-bold text-slate-800">
                    {loading ? '...' : stats.readyForPrint}
                  </p>
                </div>
                <span className="text-3xl">🏷️</span>
              </div>
            </div>

            <div className="bg-slate-200 rounded-lg shadow-lg p-4 border border-slate-300 hover:border-emerald-400/50 transition-all hover:shadow-xl">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-slate-600 text-sm">Ready for MARC</p>
                  <p className="text-2xl font-bold text-slate-800">
                    {loading ? '...' : stats.readyForExport}
                  </p>
                </div>
                <span className="text-3xl">⚡</span>
              </div>
            </div>

            <div 
              onClick={() => navigate('/loki')}
              className="bg-slate-200 rounded-lg shadow-lg p-4 border border-slate-300 hover:border-red-400/50 transition-all hover:shadow-xl cursor-pointer"
            >
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-slate-600 text-sm">Need Review</p>
                  <p className="text-2xl font-bold text-slate-800">
                    {loading ? '...' : stats.needsReview}
                  </p>
                </div>
                <span className="text-3xl">🐺</span>
              </div>
            </div>

            <div className="bg-slate-200 rounded-lg shadow-lg p-4 border border-slate-300 hover:border-purple-400/50 transition-all hover:shadow-xl">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-slate-600 text-sm">Archived</p>
                  <p className="text-2xl font-bold text-slate-800">
                    {loading ? '...' : stats.archivedBooks}
                  </p>
                </div>
                <span className="text-3xl">⚔️</span>
              </div>
            </div>
          </div>

          {/* Cataloging Workflow */}
          <div className="bg-slate-200 rounded-xl shadow-xl p-6 border border-slate-300">
            <h2 className="text-xl font-bold mb-4 text-slate-800">📖 Core Cataloging Workflow</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <button 
                onClick={() => navigate('/forge')}
                className="bg-gradient-to-br from-purple-600 to-purple-700 text-white rounded-xl p-6 hover:from-purple-500 hover:to-purple-600 transition-all transform hover:scale-105 text-left shadow-xl border border-purple-500/30"
              >
                <h3 className="text-xl font-bold mb-2">⚒️ The Forge of Odin</h3>
                <p className="text-purple-100">Start cataloging with ISBN lookup</p>
                <p className="text-xs text-purple-200 mt-2">Step 1: Add books to your collection</p>
              </button>
              
              <button 
                onClick={() => navigate('/runes')}
                className="bg-gradient-to-br from-indigo-600 to-indigo-700 text-white rounded-xl p-6 hover:from-indigo-500 hover:to-indigo-600 transition-all transform hover:scale-105 text-left shadow-xl border border-indigo-500/30"
              >
                <h3 className="text-xl font-bold mb-2">📜 The Rune Printer</h3>
                <p className="text-indigo-100">Generate barcode and spine labels</p>
                <p className="text-xs text-indigo-200 mt-2">Step 2: Print labels for physical books</p>
              </button>
              
              <button 
                onClick={() => navigate('/bifrost')}
                className="bg-gradient-to-br from-emerald-600 to-emerald-700 text-white rounded-xl p-6 hover:from-emerald-500 hover:to-emerald-600 transition-all transform hover:scale-105 text-left shadow-xl border border-emerald-500/30"
              >
                <h3 className="text-xl font-bold mb-2">🌈 The Bifrost Console</h3>
                <p className="text-emerald-100">Export MARC records to LibraryWorld</p>
                <p className="text-xs text-emerald-200 mt-2">Step 3: Import into your catalog system</p>
              </button>
            </div>
          </div>

          {/* Management Tools */}
          <div className="bg-slate-200 rounded-xl shadow-xl p-6 border border-slate-300">
            <h2 className="text-xl font-bold mb-4 text-slate-800">🔧 Management & Administration</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
              <button
                onClick={() => navigate('/mimir')}
                className="bg-gradient-to-br from-cyan-600 to-teal-700 text-white rounded-xl p-4 hover:from-cyan-500 hover:to-teal-600 transition-all transform hover:scale-105 text-left shadow-lg"
              >
                <h3 className="text-lg font-bold mb-1">💧 Mímir's Well</h3>
                <p className="text-cyan-100 text-sm">Cathedral & castle ephemera</p>
              </button>

              <button
                onClick={() => navigate('/allsight')}
                className="bg-gradient-to-br from-blue-600 to-blue-700 text-white rounded-xl p-4 hover:from-blue-500 hover:to-blue-600 transition-all transform hover:scale-105 text-left shadow-lg"
              >
                <h3 className="text-lg font-bold mb-1">🔍 Allsight</h3>
                <p className="text-blue-100 text-sm">Search, browse, and edit your collection</p>
              </button>
              
              <button 
                onClick={() => navigate('/loki')}
                className="bg-gradient-to-br from-red-600 to-red-700 text-white rounded-xl p-4 hover:from-red-500 hover:to-red-600 transition-all transform hover:scale-105 text-left shadow-lg"
              >
                <h3 className="text-lg font-bold mb-1">🐺 Loki's Chamber</h3>
                <p className="text-red-100 text-sm">Content filtering & age ratings</p>
              </button>
              
              <button 
                onClick={() => navigate('/valhalla')}
                className="bg-gradient-to-br from-amber-600 to-amber-700 text-white rounded-xl p-4 hover:from-amber-500 hover:to-amber-600 transition-all transform hover:scale-105 text-left shadow-lg"
              >
                <h3 className="text-lg font-bold mb-1">⚔️ Valhalla</h3>
                <p className="text-amber-100 text-sm">Archive completed catalog records</p>
              </button>
              
              <button 
                onClick={() => navigate('/yggdrasil')}
                className="bg-gradient-to-br from-green-600 to-green-700 text-white rounded-xl p-4 hover:from-green-500 hover:to-green-600 transition-all transform hover:scale-105 text-left shadow-lg"
              >
                <h3 className="text-lg font-bold mb-1">🌳 Yggdrasil</h3>
                <p className="text-green-100 text-sm">System settings & configuration</p>
              </button>
            </div>
          </div>

          {/* Circulation & Patron Tools */}
          <div className="bg-slate-200 rounded-xl shadow-xl p-6 border border-slate-300">
            <h2 className="text-xl font-bold mb-4 text-slate-800">🦅 Circulation & Patron Management</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
              <button
                onClick={() => navigate('/odins-ravens')}
                className="bg-gradient-to-br from-slate-700 to-slate-800 text-white rounded-xl p-4 hover:from-slate-600 hover:to-slate-700 transition-all transform hover:scale-105 text-left shadow-lg"
              >
                <h3 className="text-lg font-bold mb-1">🦅 Odin's Ravens</h3>
                <p className="text-slate-100 text-sm">Overdue notices & teacher matching</p>
              </button>

              <button
                onClick={() => navigate('/gjallarhorn')}
                className="bg-gradient-to-br from-cyan-600 to-teal-600 text-white rounded-xl p-4 hover:from-cyan-500 hover:to-teal-500 transition-all transform hover:scale-105 text-left shadow-lg"
              >
                <h3 className="text-lg font-bold mb-1">📯 Gjallarhorn</h3>
                <p className="text-cyan-100 text-sm">Library account letters & labels</p>
              </button>

              <button
                onClick={() => navigate('/mjolnir')}
                className="bg-gradient-to-br from-yellow-600 to-amber-600 text-white rounded-xl p-4 hover:from-yellow-500 hover:to-amber-500 transition-all transform hover:scale-105 text-left shadow-lg"
              >
                <h3 className="text-lg font-bold mb-1">🔨 Mjolnir</h3>
                <p className="text-yellow-100 text-sm">Damage notices & fees</p>
              </button>

              <button
                onClick={() => navigate('/admin/norns-weave')}
                className="bg-gradient-to-br from-purple-600 to-indigo-600 text-white rounded-xl p-4 hover:from-purple-500 hover:to-indigo-500 transition-all transform hover:scale-105 text-left shadow-lg"
              >
                <h3 className="text-lg font-bold mb-1">🌟 Norns' Weave</h3>
                <p className="text-purple-100 text-sm">Library booking calendar</p>
              </button>

              <button
                onClick={() => navigate('/urds-well')}
                className="bg-gradient-to-br from-violet-600 to-purple-700 text-white rounded-xl p-4 hover:from-violet-500 hover:to-purple-600 transition-all transform hover:scale-105 text-left shadow-lg"
              >
                <h3 className="text-lg font-bold mb-1">🔮 Urd's Well</h3>
                <p className="text-violet-100 text-sm">School roster management</p>
              </button>

              <button
                onClick={() => navigate('/gullveig')}
                className="bg-gradient-to-br from-amber-500 to-yellow-600 text-white rounded-xl p-4 hover:from-amber-400 hover:to-yellow-500 transition-all transform hover:scale-105 text-left shadow-lg"
              >
                <h3 className="text-lg font-bold mb-1">💰 Gullveig</h3>
                <p className="text-amber-100 text-sm">Payments & lost book reorders</p>
              </button>

              <button
                onClick={() => navigate('/hermods-path')}
                className="bg-gradient-to-br from-rose-600 to-pink-600 text-white rounded-xl p-4 hover:from-rose-500 hover:to-pink-500 transition-all transform hover:scale-105 text-left shadow-lg"
              >
                <h3 className="text-lg font-bold mb-1">📨 Hermod's Path</h3>
                <p className="text-rose-100 text-sm">Social media command centre</p>
              </button>

              </div>
          </div>

          {/* Quick Stats Overview */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Workflow Status */}
            <div className="bg-slate-200 rounded-xl shadow-xl p-6 border border-slate-300">
              <h2 className="text-xl font-bold mb-4 text-slate-800">📊 Workflow Status</h2>
              <div className="space-y-3">
                <div 
                  onClick={() => navigate('/runes')}
                  className="flex justify-between items-center p-3 bg-indigo-100 rounded-lg border border-indigo-200 cursor-pointer hover:bg-indigo-150 transition-colors"
                >
                  <span className="font-medium text-indigo-800">Labels Ready to Print</span>
                  <span className="bg-indigo-600 text-white px-3 py-1 rounded-full text-sm font-bold">
                    {stats.readyForPrint}
                  </span>
                </div>
                <div 
                  onClick={() => navigate('/bifrost')}
                  className="flex justify-between items-center p-3 bg-emerald-100 rounded-lg border border-emerald-200 cursor-pointer hover:bg-emerald-150 transition-colors"
                >
                  <span className="font-medium text-emerald-800">MARC Records Ready</span>
                  <span className="bg-emerald-600 text-white px-3 py-1 rounded-full text-sm font-bold">
                    {stats.readyForExport}
                  </span>
                </div>
                {stats.needsReview > 0 && (
                  <div 
                    onClick={() => navigate('/loki')}
                    className="flex justify-between items-center p-3 bg-red-100 rounded-lg border border-red-200 cursor-pointer hover:bg-red-150 transition-colors"
                  >
                    <span className="font-medium text-red-800">Content Needs Review</span>
                    <span className="bg-red-600 text-white px-3 py-1 rounded-full text-sm font-bold">
                      {stats.needsReview}
                    </span>
                  </div>
                )}
              </div>
            </div>

            {/* Recent Activity */}
            <div className="bg-slate-200 rounded-xl shadow-xl border border-slate-300">
              <div className="px-6 py-4 border-b border-slate-300 bg-gradient-to-r from-slate-300 to-slate-250 rounded-t-xl">
                <h2 className="text-xl font-bold text-slate-800">Recent Additions</h2>
              </div>
              <div className="divide-y divide-slate-300 max-h-80 overflow-y-auto">
                {loading ? (
                  <div className="px-6 py-8 text-center text-slate-600">
                    <p>Loading recent books...</p>
                  </div>
                ) : stats.recentBooks.length > 0 ? (
                  stats.recentBooks.map((book) => (
                    <div key={book.id} className="px-6 py-3 hover:bg-slate-150 transition-colors">
                      <div className="flex justify-between items-center">
                        <div>
                          <p className="font-medium text-slate-800 text-sm">{book.title}</p>
                          <p className="text-xs text-slate-600">
                            {book.author} • {book.barcode} • {book.location}
                          </p>
                        </div>
                        <div className="text-right">
                          <p className="text-xs text-slate-600 font-mono">
                            {book.callNumber}
                          </p>
                          <p className="text-xs text-slate-500">
                            {getRelativeTime(book.createdAt)}
                          </p>
                        </div>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="px-6 py-8 text-center text-slate-600">
                    <p className="mb-4">No books cataloged yet.</p>
                    <button 
                      onClick={() => navigate('/forge')}
                      className="bg-purple-600 text-white px-4 py-2 rounded-lg hover:bg-purple-700 text-sm"
                    >
                      Visit the Forge to Begin!
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Next Barcode Info */}
          <div className="bg-gradient-to-r from-purple-100 to-indigo-100 rounded-xl p-4 border border-purple-200">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="font-bold text-purple-800 mb-1">Next Available Barcode</h3>
                <p className="text-sm text-purple-700">Ready for your next book catalog entry</p>
              </div>
              <div className="text-right">
                <p className="text-3xl font-mono font-bold text-purple-800">{stats.nextBarcode}</p>
                <p className="text-sm text-purple-600">Sequential numbering maintained</p>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}

export default Asgard;